import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { myId } from "../lib/me";
import { supabase } from "../lib/supabase";
import { href, routeQuery } from "../lib/route";
import { monogram } from "../lib/nav";
import { Workspace } from "../components/Shell";
import {
  ALREADY_ON_THIS_DAY, WEEKDAYS, boardArrangement, boardDayKey, boardMoveWrites, boardWeeks, canAddToCell, canMoveToCell,
  cardTitle, cellDays, duplicateWeekPlan, nextPosition, pagerLabel, pagerWindow, parseBoardDayKey, removeWeekPlan,
  AUTO_WORD, DELOAD_WORD, addAutoWeek, autoWeeks, duplicateWeekRow, makeAutoPlan, makeWrittenPlan, planLine,
  removeWeekRowsPlan, toggleDeload, weekRowOf, nameProblem, ARCHIVE_SAID,
  type BoardDay, type BoardWeek, type WeekRowWrite,
} from "../lib/programBoard";
import { derivedCards, derivedKey, derivedTree, deriveWeeks, type DerivedDay } from "../lib/derivedWeek";
import { isAutoBlock, programBlockDue } from "../../../supabase/functions/_shared/coachDeload";
import { BLOCK_DEFAULT, blockColumns, blockOf, type ProgramBlock } from "../lib/programDeload";
import { ProgramDeloadBlock } from "../components/ProgramDeload";
import type { Db } from "../../../supabase/functions/_shared/nextWeekWriter";
import { Icon } from "../components/Icon";
import { SegmentRadios } from "../components/TrainingCalendar";
import { ColumnWorkout, DayColumn, useWeekTrees } from "../components/WeekDayColumn";
import { BoardCellAdd } from "../components/BoardCellAdd";
import { DayDragArea, DragItem, DropDay, MoveHandle, useDayArrangement } from "../components/DayDrag";

/** The drag's arrangement, handed to the board's render (the hook lives inside the drag area). */
function Shown({ children }: { children: (shown: Readonly<Record<string, readonly string[]>>) => ReactNode }) {
  const { arrangement } = useDayArrangement();
  return <>{children(arrangement)}</>;
}
import { copyLabel, moveHandleLabel, type DropPlan } from "../lib/dayDrag";
import { useQuerySort } from "../lib/useQuerySort";
import { usePhoneWidth } from "../lib/phoneWidth";
import {
  SCROLL_MODES, SCROLL_MODE_LABEL, WEEK_MODES, WEEK_MODE_LABEL, readStoredScrollMode, readStoredWeekMode, scrollModeFrom,
  scrollModeQueryKeeping, storeScrollMode, storeWeekMode, weekLayout, weekModeFrom, weekModeQueryKeeping,
  type ScrollMode, type WeekMode,
} from "../lib/weekView";

function viewerStore(): Storage | undefined {
  try { return window.localStorage; } catch { return undefined; }
}
import { WeekMenu, type WeekAction } from "../components/WeekMenu";
import { ProgramMenu, type ProgramAction } from "../components/ProgramMenu";
import { AssignProgramWindow } from "../components/AssignProgramWindow";

/**
 * THE PROGRAM BOARD — a multi-week block, laid out (W30(e), `docs/PROGRAMS-DESIGN.md` §4).
 *
 * 🔴 A SCREEN, NOT A WINDOW, and `backdropRoute` says so: *"the board is a screen, not a window:
 * it is where a coach works for minutes"*. The window mechanism exists for an editor whose
 * dimmed backdrop you work AGAINST, and there is nothing behind a block that helps you plan it.
 *
 * ⚠️ IT DRAWS REFERENCES, NOT WORKOUTS. Every card names a TEMPLATE from the coach's own library;
 * the block holds no content of its own, so nothing here can drift from the library. Assigning
 * calls `assign_coach_workout` once per day and each assigned day becomes a client-scoped COPY
 * (one source, 2026-08-27; copy-on-assign, V7-2).
 *
 * ⚠️ WHAT THIS COMMIT DRAWS AND WHAT IT DOES NOT, said so it is not read as a gap. The plan is
 * READ-ONLY here: the masthead, the pager, two week rows of seven cells, the cards and
 * `Assigned to:`. The cell `+`, `Add week`, the week `⋯` and the `Assign` sheet are the next
 * commit — they are WRITES, and each one owes a rehearsal and its own mutations. The rules they
 * will execute are already written and tested in `programBoard.ts`; what is missing is the
 * wiring, and the screen says so rather than drawing a control that does nothing.
 */
export function ProgramBoard({ programId, foreground = true }: {
  programId: string;
  foreground?: boolean;
}) {
  const [name, setName] = useState<string | null>(null);
  /** 11-02 — Rename in place: the field's text while the name is being edited (null = not renaming), and its refusal. */
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameSaid, setRenameSaid] = useState<string | null>(null);
  const [days, setDays] = useState<BoardDay[] | null>(null);
  /* F15 3.9 — the week rows (deload marks, auto weeks); 4.2 — the program's Deload block (`coach_programs`). */
  const [weekRows, setWeekRows] = useState<BoardWeek[]>([]);
  const [block, setBlock] = useState<ProgramBlock>(BLOCK_DEFAULT);
  const [derived, setDerived] = useState<Map<string, DerivedDay>>(new Map());
  const [deriveMs, setDeriveMs] = useState<{ read: number; derive: number } | null>(null);
  const [people, setPeople] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [firstWeek, setFirstWeek] = useState(1);
  /* One counter, one refetch. A second copy of the read called from each write is two statements
     of one query — the same reason `BodyMetricsPanel` refetches by a counter. */
  const [seq, setSeq] = useState(0);
  /* An added week exists only on the board until something is put in it: a week is `max(week_index)`
     over the DAY ROWS (§2.2), so there is no row to write for an empty one. ⚠️ THIS IS STATE AND
     NOT A LIE — it is the board's own count of trailing empty weeks, discarded on reload, which is
     correct: a week nobody planned anything into is not part of the block. */
  const [extraWeeks, setExtraWeeks] = useState(0);
  /* Which cell's picker is open, and the coach's own templates to fill it from. */
  const [picking, setPicking] = useState<{ week: number; weekday: number } | null>(null);
  const [templates, setTemplates] = useState<{ id: string; name: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  /* 🔴 W137 cut 3 (R8) — THE SAME `Minimized · Extended` (and in Extended `Scroll: Each day · Together`) as the client Week:
     the same rules (`weekView`), the same URL keys and the same per-viewer memory ("needs to look same"). */
  const [mode, setModeState] = useQuerySort<WeekMode>((q) => weekModeFrom(q, readStoredWeekMode(viewerStore())), routeQuery);
  const [scroll, setScrollState] = useQuerySort<ScrollMode>((q) => scrollModeFrom(q, readStoredScrollMode(viewerStore())), routeQuery);
  function setMode(next: WeekMode) {
    setModeState(next); storeWeekMode(viewerStore(), next);
    window.history.replaceState(null, "", `${href({ name: "program", programId })}${weekModeQueryKeeping(routeQuery(window.location.hash), next)}`);
  }
  function setScroll(next: ScrollMode) {
    setScrollState(next); storeScrollMode(viewerStore(), next);
    window.history.replaceState(null, "", `${href({ name: "program", programId })}${scrollModeQueryKeeping(routeQuery(window.location.hash), next)}`);
  }
  const isPhone = usePhoneWidth();
  /* R6b on the board: Copy puts the same template on another weekday/week of the block. */
  const [copying, setCopying] = useState<BoardDay | null>(null);

  useEffect(() => { if (foreground) heading.current?.focus(); }, [foreground]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [head, rows, assigns, wk] = await Promise.all([
        supabase.from("coach_programs").select("name, progress, deload_every, deload_automatic").eq("id", programId).limit(1),
        supabase.from("coach_program_days")
          .select("id, week_index, weekday, position, workout_id, note")
          .eq("program_id", programId),
        supabase.from("coach_program_assignments")
          .select("client_id").eq("program_id", programId),
        supabase.from("coach_program_weeks")
          .select("week_index, is_deload, derived").eq("program_id", programId),
      ]);
      if (cancelled) return;
      const failed = head.error ?? rows.error ?? assigns.error ?? wk.error;
      if (failed !== null && failed !== undefined) {
        setError(`This program could not be read. ${failed.message}`);
        return;
      }
      const headRow = (head.data ?? [])[0];
      if (headRow === undefined) {
        /* ⚠️ NOT FOUND AND NOT READABLE ARE THE SAME THING THROUGH RLS, and the sentence says the
           true one rather than guessing: an id that belongs to another coach returns zero rows
           exactly as a deleted one does. */
        setError("There is no program at this address, or it is not yours.");
        return;
      }

      /* The template NAMES, asked of the library in one flat read keyed by the ids the day rows
         carry. ⚠️ NOT an embed: a PostgREST embed is object-or-array by inferred relationship and
         arrives unordered (b4-2/3/10). An id the library does not answer for leaves the name
         NULL and `cardTitle` says so — it never draws a blank card. */
      const ids = [...new Set((rows.data ?? []).map((r) => r.workout_id))];
      const names = new Map<string, string>();
      if (ids.length > 0) {
        const lib = await supabase.from("coach_workouts").select("id, name").in("id", ids);
        if (cancelled) return;
        for (const w of lib.data ?? []) names.set(w.id, w.name);
      }

      /* `Assigned to:` — DISTINCT clients, never rows: `(program_id, client_id, start_date)` is
         unique, so one client given the block from two Mondays is two rows and one person. */
      const clientIds = [...new Set((assigns.data ?? []).map((a) => a.client_id))];
      const labels: string[] = [];
      if (clientIds.length > 0) {
        const profiles = await supabase.from("profiles")
          .select("id, first_name, last_name").in("id", clientIds);
        if (cancelled) return;
        for (const id of clientIds) {
          const p = (profiles.data ?? []).find((x) => x.id === id);
          const full = [p?.first_name, p?.last_name].filter((s) => s !== null && s !== undefined && s !== "").join(" ");
          /* A client whose profile could not be read still COUNTS — they are on the block. A
             missing name yields `?` from `monogram` rather than dropping the person. */
          labels.push(full === "" ? "?" : full);
        }
      }

      setError(null);
      setWeekRows((wk.data ?? []).map((w) => ({ weekIndex: w.week_index, isDeload: w.is_deload, derived: w.derived })));
      setBlock(blockOf(headRow));
      setName(headRow.name);
      setPeople(labels);
      setDays((rows.data ?? []).map((r) => ({
        id: r.id, weekIndex: r.week_index, weekday: r.weekday, position: r.position,
        workoutId: r.workout_id, workoutName: names.get(r.workout_id) ?? null, note: r.note,
      })));
    })();
    return () => { cancelled = true; };
  }, [programId, seq]);

  /* The picker's source: the coach's own LIVE templates, the same set the AssignPanel offers —
     `client_id is null` is what makes a row a template (V7-2: every assignment is a copy), and
     `archived_at is null` because offering an archived template is offering work the library no
     longer shows. Read once, not per open. */
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error: err } = await supabase
        .from("coach_workouts").select("id, name").eq("coach_id", await myId())
        .is("client_id", null).is("archived_at", null).order("updated_at", { ascending: false });
      if (cancelled || err !== null) return;
      setTemplates(data ?? []);
    })();
    return () => { cancelled = true; };
  }, [programId]);

  /* ⚠️ EVERY WRITE GOES THROUGH HERE, so a failure is REPORTED rather than swallowed and the
     board always refetches — the plans in `programBoard.ts` are pure and this is the only place
     that touches the database. A `void`-ed promise with no catch is how a write that never
     happened looks exactly like one that did. */
  async function run(what: string, work: () => Promise<{ message: string } | null>) {
    setBusy(true);
    const problem = await work();
    setBusy(false);
    if (problem !== null) { setError(`${what} failed. ${problem.message}`); return; }
    setError(null);
    setSeq((n) => n + 1);
  }

  /** F15 3.9 — one week-row write (the mark, auto/written): an upsert of the whole row, or its delete. */
  async function writeWeekRow(w: WeekRowWrite): Promise<{ message: string } | null> {
    if (w.kind === "delete") {
      const { error: err } = await supabase.from("coach_program_weeks").delete()
        .eq("program_id", programId).eq("week_index", w.weekIndex);
      return err;
    }
    const { error: err } = await supabase.from("coach_program_weeks").upsert({
      program_id: programId, week_index: w.row.weekIndex, is_deload: w.row.isDeload, derived: w.row.derived,
    }, { onConflict: "program_id,week_index" });
    return err;
  }

  /** 11-02 — the program's `⋯`: Rename swaps the name for a field; Archive confirms what it leaves alone, then leaves. */
  async function onProgramAction(action: ProgramAction) {
    if (action === "rename") { setRenaming(name ?? ""); setRenameSaid(null); return; }
    if (!window.confirm(`Archive ${name ?? "this program"}? ${ARCHIVE_SAID}`)) return;
    setBusy(true);
    const { error: err } = await supabase.from("coach_programs")
      .update({ archived_at: new Date().toISOString() }).eq("id", programId);
    setBusy(false);
    if (err !== null) { setError(`Archiving the program failed. ${err.message}`); return; }
    window.location.hash = href({ name: "programs" });
  }
  /** Rename's one write: the trimmed name, refused before the round trip by the table's own rule (`nameProblem`). */
  async function onRenameSave() {
    if (renaming === null) return;
    const said = nameProblem(renaming);
    if (said !== null) { setRenameSaid(said); return; }
    setBusy(true);
    const { error: err } = await supabase.from("coach_programs").update({ name: renaming.trim() }).eq("id", programId);
    setBusy(false);
    if (err !== null) { setRenameSaid(err.message); return; }
    setRenaming(null); setRenameSaid(null);
    setSeq((n) => n + 1);
  }

  /** F15 4.2 — the Deload block's one write: the program's three columns together. */
  async function onBlock(next: ProgramBlock) {
    await run("Changing the deload", async () => {
      const { error: err } = await supabase.from("coach_programs").update(blockColumns(next)).eq("id", programId);
      return err;
    });
  }

  async function onToggleDeload(week: number) {
    await run("Changing the deload", () => writeWeekRow(toggleDeload(weekRows, week)));
  }

  /** `Edit` on an auto week: its source's layout copied in, then the week is the coach's (written). */
  async function onMakeWritten(week: number) {
    const plan = makeWrittenPlan(days ?? [], weekRows, week);
    await run("Editing the week", async () => {
      if (plan.inserts.length > 0) {
        const { error: err } = await supabase.from("coach_program_days").insert(plan.inserts.map((d) => ({
          program_id: programId, week_index: d.weekIndex, weekday: d.weekday, position: d.position,
          workout_id: d.workoutId, note: d.note,
        })));
        if (err !== null) return err;
      }
      return writeWeekRow(plan.row);
    });
  }

  /** The cell's `+`: opens that day's picker, or closes it when it is the one already open. */
  function togglePicking(week: number, weekday: number) {
    setPicking((p) => (p !== null && p.week === week && p.weekday === weekday ? null : { week, weekday }));
  }

  async function addTemplate(week: number, weekday: number, workoutId: string) {
    const rows = days ?? [];
    await run("Adding the workout", async () => {
      const { error: err } = await supabase.from("coach_program_days").insert({
        program_id: programId, week_index: week, weekday,
        position: nextPosition(rows, week, weekday), workout_id: workoutId, note: null,
      });
      return err;
    });
    setPicking(null);
  }

  async function removeCard(id: string) {
    await run("Removing the workout", async () => {
      const { error: err } = await supabase.from("coach_program_days").delete().eq("id", id);
      return err;
    });
  }

  /* 🔴 W137 cut 3 — WHAT A DROP ON THE BOARD MEANS: the card's row moves cell (week_index, weekday) and/or order (position),
     written in `boardMoveWrites`' three phases (the position key is unique and not deferrable — rehearsed rolled back,
     `tools/w137-week-extended/rehearse-board.sh`: the phased move accepted, the one-phase write 23505, the same template
     twice in a cell 23505, a stranger 0 rows). The same template already in the target cell is refused before it is asked. */
  async function moveCard(plan: DropPlan): Promise<boolean> {
    const rows = days ?? [];
    if (plan.moved !== null) {
      const to = parseBoardDayKey(plan.moved.to);
      if (to === null) return false;
      if (!canMoveToCell(rows, plan.id, to.week, to.weekday)) { setError(ALREADY_ON_THIS_DAY); return false; }
    }
    const stored = Object.fromEntries(rows.map((d) => [d.id, d.position]));
    const writes = boardMoveWrites(plan, stored);
    if (writes === null) return false;
    let ok = true;
    await run("Moving the workout", async () => {
      for (const w of writes) {
        const { error: err } = await supabase.from("coach_program_days").update(w.values).eq("id", w.id);
        if (err !== null) { ok = false; return err; }
      }
      return null;
    });
    return ok;
  }

  /** `Move to…` (the 4-arrow's click): the card to the END of another cell, one write (the end is always free). */
  async function moveCardTo(day: BoardDay, week: number, weekday: number): Promise<string | null> {
    const rows = days ?? [];
    if (day.weekIndex === week && day.weekday === weekday) return null;
    if (!canMoveToCell(rows, day.id, week, weekday)) return ALREADY_ON_THIS_DAY;
    await run("Moving the workout", async () => {
      const { error: err } = await supabase.from("coach_program_days")
        .update({ week_index: week, weekday, position: nextPosition(rows, week, weekday) }).eq("id", day.id);
      return err;
    });
    return null;
  }

  async function copyCard(day: BoardDay, week: number, weekday: number): Promise<string | null> {
    const rows = days ?? [];
    if (!canAddToCell(rows, week, weekday, day.workoutId)) return ALREADY_ON_THIS_DAY;
    await run("Copying the workout", async () => {
      const { error: err } = await supabase.from("coach_program_days").insert({
        program_id: programId, week_index: week, weekday,
        position: nextPosition(rows, week, weekday), workout_id: day.workoutId, note: day.note,
      });
      return err;
    });
    return null;
  }

  async function onWeekAction(week: number, action: WeekAction) {
    const rows = days ?? [];
    const total = boardWeeks(rows, weekRows) + extraWeeks;
    if (action === "auto") {
      const plan = makeAutoPlan(rows, weekRows, week);
      if (plan === null) return;
      if (plan.deleteIds.length > 0
          && !window.confirm(`Week ${String(week)}'s workouts come off the plan; the week follows the one before it.`)) return;
      await run("Making the week auto", async () => {
        if (plan.deleteIds.length > 0) {
          const { error: err } = await supabase.from("coach_program_days").delete().in("id", plan.deleteIds);
          if (err !== null) return err;
        }
        return writeWeekRow(plan.row);
      });
      return;
    }
    if (action === "duplicate") {
      const plan = duplicateWeekPlan(rows, total, week);
      const copiedRow = duplicateWeekRow(weekRows, total, week);
      if (copiedRow !== null) {
        await run("Duplicating the week", async () => {
          const r = await writeWeekRow({ kind: "upsert", row: copiedRow });
          if (r !== null || plan.length === 0) return r;
          const { error: err } = await supabase.from("coach_program_days").insert(plan.map((d) => ({
            program_id: programId, week_index: d.weekIndex, weekday: d.weekday,
            position: d.position, workout_id: d.workoutId, note: d.note,
          })));
          return err;
        });
        return;
      }
      if (plan.length === 0) {
        /* Duplicating an EMPTY week writes no rows — correct, and it still has to add a week, or
           the coach presses a button and nothing at all happens. */
        setExtraWeeks((n) => n + 1);
        return;
      }
      await run("Duplicating the week", async () => {
        const { error: err } = await supabase.from("coach_program_days")
          .insert(plan.map((d) => ({
            program_id: programId, week_index: d.weekIndex, weekday: d.weekday,
            position: d.position, workout_id: d.workoutId, note: d.note,
          })));
        return err;
      });
      return;
    }
    const plan = removeWeekPlan(rows, total, week);
    const rowPlan = removeWeekRowsPlan(weekRows, total, week);
    await run("Removing the week", async () => {
      /* F15 3.9 — the week's own row first, then later week rows down by one, LOWEST FIRST (the same key reason). */
      if (rowPlan.deleteWeek) {
        const r = await writeWeekRow({ kind: "delete", weekIndex: week });
        if (r !== null) return r;
      }
      for (const w of rowPlan.shiftWeeks) {
        const { error: err } = await supabase.from("coach_program_weeks")
          .update({ week_index: w - 1 }).eq("program_id", programId).eq("week_index", w);
        if (err !== null) return err;
      }
      if (plan.deleteIds.length > 0) {
        const { error: err } = await supabase.from("coach_program_days")
          .delete().in("id", plan.deleteIds);
        if (err !== null) return err;
      }
      /* 🔴 LOWEST FIRST — `removeWeekPlan`'s own note. PostgREST cannot express
         `week_index = week_index - 1`, so this is one update per week; the weeks move DOWN, so k+1
         goes onto the deleted k first, emptying k+1 for k+2. (Highest first collided against
         `(program_id, week_index, weekday, position)` — measured 23505, 2026-11-01.) */
      for (const w of plan.shiftWeeks) {
        const { error: err } = await supabase.from("coach_program_days")
          .update({ week_index: w - 1 }).eq("program_id", programId).eq("week_index", w);
        if (err !== null) return err;
      }
      return null;
    });
    setExtraWeeks((n) => Math.max(0, n - 1));
  }

  const weeks = boardWeeks(days ?? [], weekRows) + extraWeeks;
  const auto = useMemo(() => autoWeeks(weekRows), [weekRows]);
  /* F15 4.2 — a program week is a deload by the BLOCK (`programBlockDue`, the planners' own): marks, else the cadence. */
  const marks = useMemo(() => weekRows.map((w) => ({ week_index: w.weekIndex, is_deload: w.isDeload })), [weekRows]);
  const deloadWeek = (week: number): boolean => programBlockDue(block, marks, week);
  /* F15 3.9 — DERIVE every auto week on open, through the writer's own chain (`derivedWeek.ts`), timed. */
  useEffect(() => {
    if (days === null) return;
    const wanted: { workoutId: string; isDeload: boolean }[] = [];
    for (const w of auto) {
      if (w > weeks) continue;
      for (const d of derivedCards(days, w, auto)) wanted.push({ workoutId: d.workoutId, isDeload: deloadWeek(w) });
    }
    if (wanted.length === 0) { setDerived(new Map()); return; }
    let cancelled = false;
    void deriveWeeks(supabase as unknown as Db, wanted, block.progress).then((r) => {
      if (cancelled) return;
      setDerived(r.days);
      setDeriveMs({ read: Math.round(r.readMs), derive: Math.round(r.deriveMs * 10) / 10 });
    });
    return () => { cancelled = true; };
  }, [days, weekRows, auto, weeks, block]);
  const win = pagerWindow(weeks, firstWeek);
  const visible: number[] = [];
  for (let w = win.first; w <= win.last; w += 1) visible.push(w);
  const layout = weekLayout({ mode, scroll, span: visible.length >= 2 ? 2 : 1, narrow: isPhone });
  const arrangement = useMemo(() => boardArrangement(days ?? [], visible), [days, visible.join(",")]);
  const storedOrder = useMemo(() => Object.fromEntries((days ?? []).map((d) => [d.id, d.position])), [days]);
  const visibleTemplates = useMemo(() => [...new Set([
    ...(days ?? []).filter((d) => visible.includes(d.weekIndex)).map((d) => d.workoutId),
    ...visible.flatMap((w) => derivedCards(days ?? [], w, auto).map((d) => d.workoutId)),
  ])], [days, visible.join(","), auto]);
  const baseTrees = useWeekTrees(visibleTemplates, layout.kind === "columns");
  /* An auto DELOAD day draws the chain's sets over its template (`derivedTree`), under its own key `auto-<id>`. */
  const trees = useMemo(() => {
    if (baseTrees.status !== "ready" || derived.size === 0) return baseTrees;
    const map = new Map(baseTrees.trees);
    for (const d of derived.values()) {
      const base = baseTrees.trees.get(d.workoutId);
      if (base !== undefined && d.kind === "changed") map.set(`auto-${d.workoutId}`, derivedTree(base, d));
    }
    return { ...baseTrees, trees: map };
  }, [baseTrees, derived]);
  /** Which tree an auto day draws: the chain's when it changed the day, else the template's own. */
  const autoTreeOf = (workoutId: string, week: number): string => {
    const d = derived.get(derivedKey(workoutId, deloadWeek(week)));
    return d !== undefined && d.kind === "changed" ? `auto-${workoutId}` : workoutId;
  };
  /* Cut 1b's rule on the board: in Extended (wide) the pager row is sticky and the day heads stick under it. */
  const pagerRef = useRef<HTMLDivElement>(null);
  const [barH, setBarH] = useState<number | null>(null);
  const stickyBar = layout.kind === "columns" && !layout.oneDayPerRow;
  useLayoutEffect(() => {
    const el = pagerRef.current;
    if (!stickyBar || el === null) { setBarH(null); return; }
    const measure = () => { setBarH(Math.round(el.getBoundingClientRect().height)); };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => { ro.disconnect(); };
  }, [stickyBar, days !== null]);
  const byId = useMemo(() => new Map((days ?? []).map((d) => [d.id, d])), [days]);
  /** A cell's cards AS THE DRAG DRAWS THEM (the gap travels across cells), else in position order. */
  const hereOf = (shown: Readonly<Record<string, readonly string[]>>, week: number, weekday: number): BoardDay[] => {
    const ids = shown[boardDayKey(week, weekday)];
    if (ids === undefined) return cellDays(days ?? [], week, weekday);
    return ids.map((id) => byId.get(id)).filter((d): d is BoardDay => d !== undefined);
  };
  /* R6b — a plain click on the 4-arrow is never dead: it opens the same small window as Copy, in its `Move to…` mode. */
  const [moving, setMoving] = useState<BoardDay | null>(null);
  /** A card's title line, in BOTH views: the template's name, then COPY and the 4-ARROW (R6b), then ×. The name opens the
      template's EDITOR (his 11-02 "when i tap a workout in a program, i can't edit the workout"; it was the read-only overview,
      Edit one click further) — the same backdrop as the overview (`backdropRoute`: both sit over the workouts list). */
  const titleLine = (day: BoardDay, wdLong: string, week: number): ReactNode => (
    <div className="program-card-top">
      <a className="program-card-link" href={href({ name: "workout", workoutId: day.workoutId })}>{cardTitle(day)}</a>
      <button type="button" className="day-copy" title="Copy" aria-label={copyLabel(cardTitle(day), `${wdLong}, week ${String(week)}`)}
              onClick={() => { setCopying(day); }}>
        <Icon name="copy" size={14} stroke={2} />
      </button>
      <MoveHandle label={moveHandleLabel(cardTitle(day), `${wdLong}, week ${String(week)}`)} onActivate={() => { setMoving(day); }} />
      <button type="button" className="program-card-x" disabled={busy}
              aria-label={`Remove ${cardTitle(day)} from ${wdLong}, week ${String(week)}`}
              onClick={() => { void removeCard(day.id); }}>×</button>
    </div>
  );

  /** F15 3.9 — a week's bar, both views: `Week N`, `Auto` when the engine fills it, `Edit` (auto), the `Deload` switch, `⋯`. */
  const weekBar = (week: number): ReactNode => {
    const row = weekRowOf(weekRows, week);
    const isAuto = auto.has(week);
    return (
      <div className="program-week-bar">
        <h2 className="ht-overline program-week-head">{`Week ${String(week)}`}</h2>
        {isAuto && <span className="program-week-tag">{AUTO_WORD}</span>}
        {isAuto && (
          <button type="button" className="ht-button ht-button-quiet program-week-edit" disabled={busy}
                  aria-label={`Edit week ${String(week)}`} onClick={() => { void onMakeWritten(week); }}>Edit</button>
        )}
        {/* His 11-02 word: the switch is MANUAL's ("show and use the deload button"); Auto proposes, so it is not drawn. */}
        {!isAutoBlock(block) && (
          <button type="button" className={`program-deload${row.isDeload ? " is-on" : ""}`} aria-pressed={row.isDeload}
                  disabled={busy} aria-label={`Deload, week ${String(week)}`}
                  onClick={() => { void onToggleDeload(week); }}>{DELOAD_WORD}</button>
        )}
        <WeekMenu week={week} canRemove={weeks > 1} canAuto={!isAuto && week > 1}
                  onChoose={(a) => { void onWeekAction(week, a); }} />
      </div>
    );
  };
  /** An auto week's card: the template's name, read-only — no copy, move, × or drag (Edit makes the week the coach's). */
  const autoCard = (day: BoardDay, week: number): ReactNode => {
    /* A deload the writer could not make on this template (its own sentence on hover) is SAID, never drawn as if made. */
    const d = derived.get(derivedKey(day.workoutId, deloadWeek(week)));
    return (
      <div className="program-card-top">
        <a className="program-card-link" href={href({ name: "workout", workoutId: day.workoutId })}>{cardTitle(day)}</a>
        {d !== undefined && d.kind === "refused" && d.isDeload && (
          <span className="ht-caption program-auto-note" title={d.reason}>As written</span>
        )}
      </div>
    );
  };

  return (
    /* PRO WEB STEP 8 — NO COLUMN 2 (Fable's ruling on step 7, replacing her own of 2026-09-20 that "a Library PAGE keeps
       its column 2"). Under W81's labelled sidebar a second rail listing the other blocks is W72's grammar left behind:
       one page, one list — the Library's Programs tab and `← All programs` are the way to another block. The column's
       own read (`coach_programs … archived_at is null`) went with it. */
    <Workspace>
    <main id="main" className="program-board">
      <header className="page-head program-board-head">
        <p className="page-eyebrow">
          <a className="ht-link" href={href({ name: "programs" })}>← All programs</a>
        </p>
        {renaming === null ? (
          <div className="program-title-row">
            <h1 className="ht-title" tabIndex={-1} ref={heading}>{name ?? "Program"}</h1>
            {name !== null && <ProgramMenu name={name} onChoose={(a) => { void onProgramAction(a); }} />}
          </div>
        ) : (
          <form className="program-rename" onSubmit={(e) => { e.preventDefault(); void onRenameSave(); }}>
            <label className="ht-visually-hidden" htmlFor="program-rename">Name</label>
            <input id="program-rename" className="ht-input" value={renaming} autoFocus maxLength={200}
                   onChange={(e) => { setRenaming(e.target.value); setRenameSaid(null); }}
                   onKeyDown={(e) => { if (e.key === "Escape") { setRenaming(null); setRenameSaid(null); } }} />
            <button type="submit" className="ht-button" disabled={busy}>Save</button>
            <button type="button" className="ht-button ht-button-quiet" onClick={() => { setRenaming(null); setRenameSaid(null); }}>Cancel</button>
            {renameSaid !== null && <p className="ht-caption program-error" role="alert">{renameSaid}</p>}
          </form>
        )}
        {/* F15 3.9 — THE PLAN LINE: length, how many weeks are written, the deload weeks (or the workouts' default). */}
        {days !== null && (
          <p className="ht-caption program-plan" data-derive-ms={deriveMs === null ? undefined : `${String(deriveMs.read)}+${String(deriveMs.derive)}`}>
            {planLine(weeks, weekRows, isAutoBlock(block))}
          </p>
        )}
        {days !== null && (
          <ProgramDeloadBlock block={block} busy={busy} onChange={(next) => { void onBlock(next); }} />
        )}
        {people.length > 0 && (
          <p className="program-assigned">
            <span className="ht-overline">Assigned to</span>
            <span className="program-avatars">
              {people.map((who, i) => (
                /* The monogram disc, the one the account row already uses — `CoachAvatar`'s own
                   lesson is that a shared renderer takes ONE caller's value and makes it
                   everybody's, so this reaches for the shared FUNCTION and carries its own fill
                   rather than importing a component styled for another list. */
                <span key={`${who}-${String(i)}`} className="program-avatar" title={who}>
                  <span aria-hidden="true">{monogram(who)}</span>
                  <span className="ht-visually-hidden">{who}</span>
                </span>
              ))}
            </span>
          </p>
        )}
      </header>

      {error !== null && <p className="ht-callout program-error" role="alert">{error}</p>}
      {days === null && error === null && (
        <p className="ht-callout loading-line" aria-live="polite">Loading…</p>
      )}

      {days !== null && error === null && (
        <DayDragArea arrangement={arrangement} stored={storedOrder} titleOf={(id) => { const d = byId.get(id); return d === undefined ? "Workout" : cardTitle(d); }}
                     onDrop={moveCard} disabled={busy}>
        <div className={`program-days week-sticky-scope${stickyBar ? " has-sticky-bar" : ""}`}
             style={barH === null ? undefined : { "--calendar-bar-h": `${String(barH)}px` } as React.CSSProperties}>
          <div ref={pagerRef} className="program-pager week-sticky-bar">
            {/* ⚠️ REAL BUTTONS, DISABLED AT THE ENDS RATHER THAN ABSENT. A control that vanishes
                moves everything beside it and gives a keyboard coach nothing to land on; a
                disabled one keeps the row still and says why it cannot be pressed. */}
            <button type="button" className="ht-button ht-button-quiet"
                    disabled={win.first <= 1}
                    onClick={() => setFirstWeek((w) => Math.max(1, w - 2))}>
              <span aria-hidden="true">‹</span>
              <span className="ht-visually-hidden">Earlier weeks</span>
            </button>
            <p className="program-pager-label" aria-live="polite">{pagerLabel(win, weeks)}</p>
            <button type="button" className="ht-button ht-button-quiet"
                    disabled={win.last >= weeks}
                    onClick={() => setFirstWeek((w) => w + 2)}>
              <span aria-hidden="true">›</span>
              <span className="ht-visually-hidden">Later weeks</span>
            </button>
            {/* ⚠️ `Add week` WRITES NOTHING, AND THAT IS THE MODEL RATHER THAN A SHORTCUT. A
                week is `max(week_index)` over the DAY ROWS (§2.2) — the head stores no count —
                so an empty week has no row to write and exists only as somewhere to drop the
                first template. It also pages you to it, or the button appears to do nothing. */}
            {/* F15 3.9 — `Add week` adds an AUTO week (a row): nobody types 52 weeks; `Edit` makes it written. */}
            <button type="button" className="ht-button program-add-week" disabled={busy || weeks >= 52}
                    onClick={() => {
                      void run("Adding a week", () => writeWeekRow(addAutoWeek(weeks)));
                      setFirstWeek(Math.max(1, weeks));
                    }}>Add week</button>
            {/* ⚠️ `Assign` IS THE SCREEN'S ONE PRIMARY — §13 caps a surface at one, and this is
                the thing a coach came to the board to be able to do. It is DISABLED on a block
                with no days: assigning nothing writes nothing and would report "Nothing to
                assign", which is a sentence nobody should have to read to learn that. */}
            <button type="button" className="ht-button ht-button-primary program-assign"
                    disabled={(days ?? []).length === 0}
                    onClick={() => setAssigning(true)}>Assign</button>
            {/* W137 cut 3 (R8) — the client Week's own two controls, here beside the pager. */}
            <SegmentRadios name={`board-mode-${programId}`} legend="View" className="calendar-mode"
                           options={WEEK_MODES.map((m) => ({ value: m, label: WEEK_MODE_LABEL[m] }))}
                           value={mode} onChange={setMode} />
            {layout.scrollControl && (
              <SegmentRadios name={`board-scroll-${programId}`} legend="Scroll" showLegend className="calendar-scroll-mode"
                             options={SCROLL_MODES.map((m) => ({ value: m, label: SCROLL_MODE_LABEL[m] }))}
                             value={scroll} onChange={setScroll} />
            )}
          </div>

          <Shown>{(shown) => (<>
          {layout.kind === "squares" && visible.map((week) => (
            <section className={`program-week${auto.has(week) ? " is-auto" : ""}`} key={week} aria-label={`Week ${String(week)}`}>
              {/* ⚠️ THE LAST REMAINING WEEK CANNOT BE REMOVED — `canRemove` drops the item rather than disabling it. */}
              {weekBar(week)}
              <ol className="program-cells">
                {auto.has(week) && WEEKDAYS.map((wd) => {
                  const here = derivedCards(days, week, auto).filter((d) => d.weekday === wd.weekday).sort((a, b) => a.position - b.position);
                  return (
                    <li key={wd.weekday} className="program-cell is-auto">
                      <p className="program-cell-day" aria-hidden="true">{wd.short}</p>
                      <span className="ht-visually-hidden">{`${wd.long}, week ${String(week)}`}</span>
                      {here.length === 0 ? null : (
                        <ul className="program-cards">
                          {here.map((day) => <li key={day.id} className="program-card is-auto">{autoCard(day, week)}</li>)}
                        </ul>
                      )}
                    </li>
                  );
                })}
                {!auto.has(week) && WEEKDAYS.map((wd) => {
                  const here = hereOf(shown, week, wd.weekday);
                  return (
                    <DropDay key={wd.weekday} dayKey={boardDayKey(week, wd.weekday)} ids={here.map((d) => d.id)}>{(dropRef, isOver) => (
                    <li ref={dropRef} className={`program-cell${isOver ? " is-drop-over" : ""}`}>
                      <p className="program-cell-day" aria-hidden="true">{wd.short}</p>
                      {/* The full weekday for anyone not reading the three letters. */}
                      <span className="ht-visually-hidden">{`${wd.long}, week ${String(week)}`}</span>
                      {/* ⚠️ AN EMPTY CELL SAYS NOTHING, AND THE WORD IT USED TO SAY WAS WRONG
                          (Fable, 2026-09-20). A blank day on a COACH'S TEMPLATE is a day with
                          nothing assigned; what the client is told about it is the phone's to
                          decide, and printing `Rest` here made a planning gap look like a
                          prescription. Theirs draws empty cells empty. The cell's `+` is the
                          only thing an empty day offers. */}
                      {here.length === 0 ? null : (
                        <ul className="program-cards">
                          {here.map((day) => (
                            <DragItem key={day.id} id={day.id} className="program-card">
                              {/* W137 cut 3 — the card's title line: the template's overview, then Copy and the 4-arrow (R6b),
                                  then × (a REFERENCE removed — nothing a client holds is touched). */}
                              {titleLine(day, wd.long, week)}
                            </DragItem>
                          ))}
                        </ul>
                      )}

                      {/* THE CELL'S `+` — §4: it opens the picker of the coach's own live templates. ONE component for both views
                          (`BoardCellAdd`, his 10-31 report): drawn at rest on an EMPTY day, hover-revealed on a filled one. */}
                      <BoardCellAdd view="cell" week={week} weekday={wd.weekday} dayName={wd.long} empty={here.length === 0}
                                    open={picking !== null && picking.week === week && picking.weekday === wd.weekday}
                                    busy={busy} days={days} templates={templates}
                                    onToggle={() => { togglePicking(week, wd.weekday); }} onClose={() => { setPicking(null); }}
                                    onPick={(id) => { void addTemplate(week, wd.weekday, id); }} />
                    </li>
                    )}</DropDay>
                  );
                })}
              </ol>
            </section>
          ))}

          {/* 🔴 W137 cut 3 (R8) — EXTENDED: the client Week's day column, here for a block's weeks — each card drawn as its
              TEMPLATE's overview (notes and rests the same), the same per-day scroll and `↑`, the same drag. */}
          {layout.kind === "columns" && (
            <div className={["week-ext", `is-rows-${layout.rowHeight}`, layout.dayScrolls ? "is-day-scroll" : "is-page-scroll",
              layout.stickyHeads ? "has-sticky-heads" : "", layout.oneDayPerRow ? "is-one-per-row" : ""].filter(Boolean).join(" ")}>
              {visible.map((week) => (
                <section className={`week-ext-week program-week${auto.has(week) ? " is-auto" : ""}`} key={week} aria-label={`Week ${String(week)}`}>
                  {weekBar(week)}
                  <div className="week-ext-row">
                    {auto.has(week) && WEEKDAYS.map((wd) => {
                      const here = derivedCards(days, week, auto).filter((d) => d.weekday === wd.weekday).sort((a, b) => a.position - b.position);
                      return (
                        <DayColumn key={wd.weekday} dayKey={boardDayKey(week, wd.weekday)} scrolls={layout.dayScrolls}
                                   label={`${wd.long}, week ${String(week)}`} topLabel={`Back to top of ${wd.long}, week ${String(week)}`}
                                   className="is-auto"
                                   head={<div className="calendar-day-head"><span className="ht-data-small calendar-day-num">{wd.long}</span></div>}>
                          <ul className="week-col-items">
                            {here.map((day) => (
                              <li key={day.id} className="ht-caption week-col-item is-auto">
                                <ColumnWorkout tree={autoTreeOf(day.workoutId, week)} trees={trees}
                                               title={<div className="week-col-title">{autoCard(day, week)}</div>} />
                              </li>
                            ))}
                          </ul>
                        </DayColumn>
                      );
                    })}
                    {!auto.has(week) && WEEKDAYS.map((wd) => {
                      const here = hereOf(shown, week, wd.weekday);
                      return (
                        <DropDay key={wd.weekday} dayKey={boardDayKey(week, wd.weekday)} ids={here.map((d) => d.id)}>{(dropRef, isOver) => (
                          <DayColumn dayKey={boardDayKey(week, wd.weekday)} rootRef={dropRef} scrolls={layout.dayScrolls}
                                     label={`${wd.long}, week ${String(week)}`} topLabel={`Back to top of ${wd.long}, week ${String(week)}`}
                                     className={isOver ? "is-drop-over" : undefined}
                                     head={<div className="calendar-day-head"><span className="ht-data-small calendar-day-num">{wd.long}</span></div>}>
                            <ul className="week-col-items">
                              {here.map((day) => (
                                <DragItem key={day.id} id={day.id} className="ht-caption week-col-item">
                                  <ColumnWorkout tree={day.workoutId} trees={trees} title={<div className="week-col-title">{titleLine(day, wd.long, week)}</div>} />
                                </DragItem>
                              ))}
                            </ul>
                            {/* His 10-31 report: Extended had NO add. The same `BoardCellAdd` as Minimized, at the foot of the day's
                                workouts — `+ Workout` at rest on an empty day, revealed by hover/focus on a filled one. */}
                            <BoardCellAdd view="column" week={week} weekday={wd.weekday} dayName={wd.long} empty={here.length === 0}
                                          open={picking !== null && picking.week === week && picking.weekday === wd.weekday}
                                          busy={busy} days={days} templates={templates}
                                          onToggle={() => { togglePicking(week, wd.weekday); }} onClose={() => { setPicking(null); }}
                                          onPick={(id) => { void addTemplate(week, wd.weekday, id); }} />
                          </DayColumn>
                        )}</DropDay>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          )}
          </>)}</Shown>
        </div>
        </DayDragArea>
      )}
      {moving !== null && (
        <BoardCopyWindow day={moving} weeks={weeks + 1} mode="move" onClose={() => { setMoving(null); }}
                         onCopy={async (week, weekday) => { const why = await moveCardTo(moving, week, weekday); if (why === null) setMoving(null); return why; }} />
      )}
      {copying !== null && (
        <BoardCopyWindow day={copying} weeks={weeks + 1} onClose={() => { setCopying(null); }}
                         onCopy={async (week, weekday) => { const why = await copyCard(copying, week, weekday); if (why === null) setCopying(null); return why; }} />
      )}
      <AssignProgramWindow
        programId={programId} programName={name ?? "Program"}
        days={(days ?? []).map((d) => ({
          weekIndex: d.weekIndex, weekday: d.weekday, position: d.position,
          workoutId: d.workoutId, note: d.note,
        }))}
        weeks={weeks}
        deloadWeeks={isAutoBlock(block) ? []
          : weekRows.filter((w) => w.isDeload && !auto.has(w.weekIndex)).map((w) => w.weekIndex)}
        autoWeekCount={[...auto].filter((w) => w <= weeks).length}
        open={assigning}
        onClose={() => setAssigning(false)}
        /* The board refetches: `Assigned to:` is reading the table this just wrote to. */
        onAssigned={() => setSeq((n) => n + 1)} />
    </main>
    </Workspace>
  );
}

/**
 * W137 R6b on the board — COPY TO…: the same template onto another weekday and week of the block (a new
 * `coach_program_days` row, `position` = last in that cell). Rehearsed rolled back (`rehearse-board.sh` B2 accepted, B3 the
 * same template twice in a cell 23505); the board refuses that case before it asks, in its own words.
 */
function BoardCopyWindow({ day, weeks, mode = "copy", onClose, onCopy }: {
  day: BoardDay;
  /** `copy` (Copy to…) or `move` (Move to… — the 4-arrow's plain click): the same small window, two verbs. */
  mode?: "copy" | "move";
  /** The weeks a copy may go to: the block's, and one past it (a copy may start the next week). */
  weeks: number;
  onClose: () => void;
  onCopy: (week: number, weekday: number) => Promise<string | null>;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [week, setWeek] = useState(day.weekIndex);
  const [weekday, setWeekday] = useState(day.weekday === 7 ? 1 : day.weekday + 1);
  const [said, setSaid] = useState<string | null>(null);
  useEffect(() => { const el = ref.current; if (el !== null && !el.open) el.showModal(); }, []);
  return (
    <dialog ref={ref} className="ht-window day-window board-copy-window" aria-labelledby="board-copy-title"
            onCancel={(e) => { e.preventDefault(); onClose(); }}>
      <div className="ht-window-frame">
        <header className="ht-window-head">
          <div className="ht-window-titles">
            <p className="page-eyebrow">{cardTitle(day)}</p>
            <h2 className="ht-title" id="board-copy-title">{mode === "copy" ? "Copy to…" : "Move to…"}</h2>
          </div>
          <button type="button" className="ht-button ht-button-quiet ht-window-close" onClick={onClose}>Close</button>
        </header>
        <div className="ht-window-body day-window-body">
          <div className="day-control">
            <label className="ht-field-label" htmlFor="board-copy-week">Week</label>
            <select id="board-copy-week" className="ht-input" value={week} onChange={(e) => setWeek(Number(e.target.value))}>
              {Array.from({ length: Math.min(weeks, 52) }, (_, i) => i + 1).map((w) => <option key={w} value={w}>{`Week ${String(w)}`}</option>)}
            </select>
            <label className="ht-field-label" htmlFor="board-copy-day">Day</label>
            <select id="board-copy-day" className="ht-input" value={weekday} onChange={(e) => setWeekday(Number(e.target.value))}>
              {WEEKDAYS.map((wd) => <option key={wd.weekday} value={wd.weekday}>{wd.long}</option>)}
            </select>
            <button type="button" className="ht-button" onClick={() => { void onCopy(week, weekday).then(setSaid); }}>
              {mode === "copy" ? "Copy" : "Move"}
            </button>
            {said !== null && <span className="ht-caption day-hint" role="status">{said}</span>}
          </div>
        </div>
      </div>
    </dialog>
  );
}
